// キャッシュエントリーの型定義
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

/**
 * 汎用キャッシュマネージャー
 * メモリ内キャッシュでTTL（Time To Live）機能付き
 */
export class CacheManager {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5分

  /**
   * キャッシュに値を設定
   * @param key キー
   * @param data データ
   * @param ttl 有効期限（ミリ秒）、デフォルト5分
   */
  set<T>(key: string, data: T, ttl = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    });
  }

  /**
   * キャッシュから値を取得
   * @param key キー
   * @returns データまたはnull（期限切れ・存在しない場合）
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return entry.data as T;
  }

  /**
   * キャッシュを無効化
   * @param pattern パターン指定時は部分マッチするキーを削除、未指定時は全削除
   */
  invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }

    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * キャッシュの統計情報を取得
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }
}

// グローバルキャッシュインスタンス
export const cache = new CacheManager();
